import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Input,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { InvoiceAdjustmentActionTypeEnum, InvoiceAdjustmentTypeEnum } from 'parkchirp-shared/enums';
import { useEffect, useState } from 'react';

import {
	AMOUNT_ADJUSTMENT_INVOICE_ACTION,
	GET_INVOICE_LINE_ITEM_ENTRY_AMOUNTS_BY_INVOICE_ID,
	MANUAL_ADJUSTMENT_INVOICE_ACTION,
} from '../../../queries';
import { paramToSlug } from '../../../utils';
import useAdjustInvoiceContext from './useAdjustInvoiceContext';

// adjustment amount when partial, always positive
// when it comes to a custom adjustment amount by line item
// the adjustment can be negative. we care about the sign when
// doing a custom adjustment so the value that the invoice's amount
// should change by is calculated elsewhere and stored in the context
export const AdjustInvoiceNotes = ({ id, refetch, setShowTable, setShowSnackbar }) => {
	const { setIsAdjustingEachLineItem, getLineItemAdjustmentTotal, getAllLineItemAdjustmentsFromMap } =
		useAdjustInvoiceContext();
	const [adjustInvoice, { loading: adjustInvoiceLoading }] = useMutation(AMOUNT_ADJUSTMENT_INVOICE_ACTION);
	const [manuallyAdjustInvoice, { loading: manuallyAdjustInvoiceLoading }] = useMutation(
		MANUAL_ADJUSTMENT_INVOICE_ACTION,
	);
	const [adjustmentType, setAdjustmentType] = useState<string | null>('');
	const [amount, setAmount] = useState<number>(null);
	const [adjustmentAction, setAdjustmentAction] = useState<string>('CUSTOM');
	const [amountTax1, setAmountTax1] = useState<number>(null);
	const [amountTax2, setAmountTax2] = useState<number>(null);
	const [amountTax3, setAmountTax3] = useState<number>(null);
	const [amountTax4, setAmountTax4] = useState<number>(null);

	const {
		loading: invoiceAmountsLoading,
		data: { getInvoiceLineItemEntryAmountsByInvoiceId: invoiceAmounts = {} } = {},
		refetch: refetchInvoiceAmounts,
	} = useQuery(GET_INVOICE_LINE_ITEM_ENTRY_AMOUNTS_BY_INVOICE_ID, {
		context: {
			apiVersion: 'v2',
			fetchPolicy: 'no-cache',
		},
		variables: {
			invoiceId: id,
		},
	});

	function getMultiplerForAmounts() {
		// we return 1 here because if a user is adjusting an invoice by line item, we let the sign
		// of the total adjustment amount determine what sign it should be, skipping using the multiplier
		if (InvoiceAdjustmentActionTypeEnum.CUSTOM === InvoiceAdjustmentActionTypeEnum[adjustmentAction]) {
			return 1;
		}
		let multiplier = -1;
		if (
			[
				InvoiceAdjustmentTypeEnum.ADJUSTMENT_POSITIVE,
				InvoiceAdjustmentTypeEnum.TERMINATION_ADJUSTMENT_POSITIVE,
			].includes(InvoiceAdjustmentTypeEnum[adjustmentType])
		) {
			multiplier = 1;
		}
		return multiplier;
	}

	useEffect(() => {
		setIsAdjustingEachLineItem(adjustmentAction === 'CUSTOM');
		if (adjustmentAction === 'FULL') {
			setAmount(invoiceAmounts.amount);
			setAmountTax1(invoiceAmounts.amountTax1);
			setAmountTax2(invoiceAmounts.amountTax2);
			setAmountTax3(invoiceAmounts.amountTax3);
			setAmountTax4(invoiceAmounts.amountTax4);
		} else if (adjustmentAction === 'CUSTOM') {
			// the below function will handle adding/subtracting line items for us
			const totals = getLineItemAdjustmentTotal();
			setAmount(totals.amount);
			setAmountTax1(totals.amountTax1);
			setAmountTax2(totals.amountTax2);
			setAmountTax3(totals.amountTax3);
			setAmountTax4(totals.amountTax4);
		} else {
			setAmount(null);
			setAmountTax1(null);
			setAmountTax2(null);
			setAmountTax3(null);
			setAmountTax4(null);
		}
	}, [adjustmentAction, invoiceAmounts, setIsAdjustingEachLineItem, getLineItemAdjustmentTotal, id]);

	function sumAdjustmentAmount() {
		return (amount || 0) + (amountTax1 || 0) + (amountTax2 || 0) + (amountTax3 || 0) + (amountTax4 || 0);
	}

	function formatAmount(valueInCents: number) {
		let valueInCentsToUse = valueInCents;
		if (Number.isNaN(valueInCents) || typeof valueInCents !== 'number') {
			valueInCentsToUse = 0;
		}
		let formattedValue = (Math.abs(valueInCentsToUse) / 100).toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
		});
		if (valueInCentsToUse < 0) {
			// wrap the value in parenthesis since it's a negative value
			formattedValue = `(${formattedValue})`;
		}
		return formattedValue;
	}

	function adjustmentIsValid() {
		// sumAdjustmentAmount returns a positive value if we are adjusting
		// all line items to zero or entering a single adjustment amount for
		// all line items. If we are adjusting each line item in a custom way,
		// a positive or negative value is returned. The reason for this difference
		// in sign is because its hard to keep track of each custom adjustment's type.
		// Instead, we use a positive or negative number to show if the custom
		// adjustment's value is a net increase or decrease respectively.
		//
		// The invoiceAdjustmentIsNegative helps us ensure that we aren't
		// adjusting an invoice in a way that would cause the balance to be negative.

		const normalizedAmount = amount || 0;
		const normalizedAmountTax1 = amountTax1 || 0;
		const normalizedAmountTax2 = amountTax2 || 0;
		const normalizedAmountTax3 = amountTax3 || 0;
		const normalizedAmountTax4 = amountTax4 || 0;

		const adjustmentAmount = sumAdjustmentAmount();

		let invoiceAdjustmentIsNegative = adjustmentAmount < 0;
		if (
			[InvoiceAdjustmentActionTypeEnum.PARTIAL, InvoiceAdjustmentActionTypeEnum.FULL].includes(
				InvoiceAdjustmentActionTypeEnum[adjustmentAction],
			)
		) {
			invoiceAdjustmentIsNegative = getMultiplerForAmounts() === -1;
		}

		if (invoiceAdjustmentIsNegative) {
			if (Math.abs(adjustmentAmount) > invoiceAmounts.grossAmount) {
				const formattedInvoiceAmount = formatAmount(invoiceAmounts.grossAmount);
				const formattedAdjustmentAmount = formatAmount(Math.abs(adjustmentAmount));
				setShowSnackbar({
					message:
						`Adjustment amount of ${formattedAdjustmentAmount} cannot exceed invoice` +
						` amount of ${formattedInvoiceAmount}.`,
					severity: 'error',
					autoHideDuration: null,
				});
				return false;
			}
			const fieldsThatWouldCauseNegativeBalance = [
				{
					label: 'Net Amount',
					value: normalizedAmount < 0 && Math.abs(normalizedAmount) > invoiceAmounts.amount,
				},
				{
					label: 'Amount Tax 1',
					value: normalizedAmountTax1 < 0 && Math.abs(normalizedAmountTax1) > invoiceAmounts.amountTax1,
				},
				{
					label: 'Amount Tax 2',
					value: normalizedAmountTax2 < 0 && Math.abs(normalizedAmountTax2) > invoiceAmounts.amountTax2,
				},
				{
					label: 'Amount Tax 3',
					value: normalizedAmountTax3 < 0 && Math.abs(normalizedAmountTax3) > invoiceAmounts.amountTax3,
				},
				{
					label: 'Amount Tax 4',
					value: normalizedAmountTax4 < 0 && Math.abs(normalizedAmountTax4) > invoiceAmounts.amountTax4,
				},
			];
			const hasValuesTooLarge = fieldsThatWouldCauseNegativeBalance.filter(({ value }) => !!value);
			if (hasValuesTooLarge.length) {
				const errorAsString = hasValuesTooLarge.map(({ label }) => label).join(', ');
				setShowSnackbar({
					message:
						`Unable to submit adjustment as the field(s) ${errorAsString}` +
						` are larger than the invoice.`,
					severity: 'error',
					autoHideDuration: null,
				});
				return false;
			}
		}
		if (InvoiceAdjustmentActionTypeEnum.PARTIAL === InvoiceAdjustmentActionTypeEnum[adjustmentAction]) {
			// as mentioned above, the adjustment value when we
			// enter a single adjustment value (called PARTIAL) is
			// always positive, so we dont need to do Math.abs and
			// the below ensures a positive value was entered.
			const fieldsThatMustBeNonNegative = [
				{ label: 'Net Amount', value: amount },
				{ label: 'Amount Tax 1', value: amountTax1 },
				{ label: 'Amount Tax 2', value: amountTax2 },
				{ label: 'Amount Tax 3', value: amountTax3 },
				{ label: 'Amount Tax 4', value: amountTax4 },
			];
			const hasNegativeValues = fieldsThatMustBeNonNegative.filter(({ value }) => value < 0);
			if (hasNegativeValues.length) {
				const errorAsString = hasNegativeValues.map(({ label }) => label).join(', ');
				setShowSnackbar({
					message: `Unable to submit adjustment due to negative field(s): ${errorAsString}`,
					severity: 'error',
					autoHideDuration: null,
				});
				return false;
			}
		}
		return true;
	}

	const handleSubmit = () => {
		if (!adjustmentIsValid()) {
			return;
		}

		const onCompleted = () => {
			setShowSnackbar({
				message: 'Invoice adjustment successful.',
				severity: 'success',
			});
			setShowTable(false);
			refetchInvoiceAmounts();
			refetch();
		};
		const onError = (error) => {
			setShowSnackbar({
				message: `Unable to adjust invoice: ${error}`,
				severity: 'error',
				autoHideDuration: null,
			});
		};
		if (adjustmentAction === 'CUSTOM') {
			const adjustments = getAllLineItemAdjustmentsFromMap();
			manuallyAdjustInvoice({
				context: {
					apiVersion: 'v2',
				},
				variables: {
					invoiceId: id,
					adjustments: adjustments.map((x) => ({
						amount: x.amount,
						amountTax1: x.amountTax1,
						amountTax2: x.amountTax2,
						amountTax3: x.amountTax3,
						amountTax4: x.amountTax4,
						adjustmentType: x.adjustmentType,
						lineItemId: x.lineItemId,
					})),
				},
				onCompleted,
				onError,
			});
		} else {
			adjustInvoice({
				context: {
					apiVersion: 'v2',
				},
				variables: {
					invoiceId: id,
					adjustmentType,
					adjustment: {
						amount: amount || 0,
						amountTax1: amountTax1 || 0,
						amountTax2: amountTax2 || 0,
						amountTax3: amountTax3 || 0,
						amountTax4: amountTax4 || 0,
					},
					adjustmentAction,
				},
				onCompleted,
				onError,
			});
		}
	};

	function renderInitialAmounts() {
		return (
			<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
				<TableCell>Current Balance</TableCell>
				<TableCell data-cy='current-balance-net-cell'>{formatAmount(invoiceAmounts.amount)}</TableCell>
				{[1, 2, 3, 4].map((taxId) => (
					<TableCell key={taxId} data-cy={`current-balance-tax-${taxId}-cell`}>
						{formatAmount(invoiceAmounts[`amountTax${taxId}`])}
					</TableCell>
				))}
				<TableCell data-cy='current-balance-gross-cell'>{formatAmount(invoiceAmounts.grossAmount)}</TableCell>
			</TableRow>
		);
	}

	function formatAndSaveValue(setterFunction: (value: number) => void, value: string) {
		if (['', undefined, null].includes(value)) {
			setterFunction(null);
			return;
		}
		const formattedValue = value.replace(/[^0-9.]/g, '');
		if (formattedValue?.match(/\./g)) {
			const [, decimalValues] = formattedValue.split('.');
			if (decimalValues?.length > 2) return;
		}
		const updatedValue = Number(formattedValue) * 100;

		setterFunction(Number(updatedValue.toFixed(0)));
	}

	function renderInputOrReadonlyAdjustment(key: string, value: number, setterfunction: (value: number) => void) {
		const dataCy = `adjustment-${paramToSlug(key)}-cell`;
		if (InvoiceAdjustmentActionTypeEnum.FULL === InvoiceAdjustmentActionTypeEnum[adjustmentAction]) {
			return (
				<TableCell key={key} data-cy={dataCy}>
					{formatAmount(value * -1)}
				</TableCell>
			);
		}
		if (InvoiceAdjustmentActionTypeEnum.CUSTOM === InvoiceAdjustmentActionTypeEnum[adjustmentAction]) {
			return (
				<TableCell key={key} data-cy={dataCy}>
					{formatAmount(value)}
				</TableCell>
			);
		}

		return (
			<TableCell key={key}>
				<Input
					type='number'
					disabled={!adjustmentType}
					// @ts-ignore
					value={['', undefined, null].includes(value) ? '' : value / 100}
					inputProps={{ min: 0 }}
					onChange={(e) => formatAndSaveValue(setterfunction, e.target.value)}
					data-cy={dataCy}
				/>
			</TableCell>
		);
	}

	function renderAdjustmentRow() {
		const amounts = [
			{
				key: 'amount',
				value: amount,
				setter: setAmount,
			},
			{
				key: 'amountTax1',
				value: amountTax1,
				setter: setAmountTax1,
			},
			{
				key: 'amountTax2',
				value: amountTax2,
				setter: setAmountTax2,
			},
			{
				key: 'amountTax3',
				value: amountTax3,
				setter: setAmountTax3,
			},
			{
				key: 'amountTax4',
				value: amountTax4,
				setter: setAmountTax4,
			},
		];
		const multiplier = getMultiplerForAmounts();
		return (
			<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
				<TableCell>Adjustment</TableCell>
				{amounts.map(({ key, value, setter }) => renderInputOrReadonlyAdjustment(key, value, setter))}
				{/* show negative for the below if we are doing full */}
				<TableCell data-cy='adjustment-gross-cell'>
					{formatAmount(multiplier * sumAdjustmentAmount())}
				</TableCell>
			</TableRow>
		);
	}

	function renderAdjustedAmounts() {
		function formatAmountWithCorrectSign(originalAmount, adjustedAmount) {
			if (adjustmentAction === 'FULL') {
				return formatAmount(0);
			}
			if (adjustmentAction === 'PARTIAL') {
				const multiplier = getMultiplerForAmounts();
				return formatAmount(originalAmount + adjustedAmount * multiplier);
			}
			return formatAmount(originalAmount + adjustedAmount);
		}
		return (
			<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
				<TableCell>Adjusted Balance</TableCell>
				<TableCell data-cy='adjusted-balance-net-cell'>
					{formatAmountWithCorrectSign(invoiceAmounts.amount, amount)}
				</TableCell>
				<TableCell data-cy='adjusted-balance-tax-1-cell'>
					{formatAmountWithCorrectSign(invoiceAmounts.amountTax1, amountTax1)}
				</TableCell>
				<TableCell data-cy='adjusted-balance-tax-2-cell'>
					{formatAmountWithCorrectSign(invoiceAmounts.amountTax2, amountTax2)}
				</TableCell>
				<TableCell data-cy='adjusted-balance-tax-3-cell'>
					{formatAmountWithCorrectSign(invoiceAmounts.amountTax3, amountTax3)}
				</TableCell>
				<TableCell data-cy='adjusted-balance-tax-4-cell'>
					{formatAmountWithCorrectSign(invoiceAmounts.amountTax4, amountTax4)}
				</TableCell>
				<TableCell data-cy='adjusted-balance-gross-cell'>
					{formatAmountWithCorrectSign(invoiceAmounts.grossAmount, sumAdjustmentAmount())}
				</TableCell>
			</TableRow>
		);
	}

	function renderAdjustmentTypeDropdown() {
		let keysToUse = Object.keys(InvoiceAdjustmentTypeEnum);
		if (adjustmentAction === 'FULL') {
			// return only the negative adjustment types since the user wants to adjust to zero
			keysToUse = Object.keys(InvoiceAdjustmentTypeEnum).filter(
				(key) =>
					![
						InvoiceAdjustmentTypeEnum.ADJUSTMENT_POSITIVE,
						InvoiceAdjustmentTypeEnum.TERMINATION_ADJUSTMENT_POSITIVE,
					].includes(InvoiceAdjustmentTypeEnum[key]),
			);
		}
		return (
			<Box style={{ width: '33%', minHeight: '40px', alignSelf: 'center' }}>
				<InputLabel id='adjustment-type-select-label'>Adjustment Type</InputLabel>
				<Select
					labelId='adjustment-type-select-label'
					value={adjustmentType}
					onChange={(e) => setAdjustmentType(e.target.value)}
					sx={{
						minWidth: '254px',
					}}
					data-cy='adjustment-type-select-menu'
				>
					{keysToUse.map((key) => (
						<MenuItem key={key} value={key}>
							{InvoiceAdjustmentTypeEnum[key]}
						</MenuItem>
					))}
					<MenuItem value='' />
				</Select>
			</Box>
		);
	}

	function maybeRenderAdjustmentDetails() {
		return (
			<>
				<Box display='flex' flexDirection='row' justifyContent='start'>
					<Box
						style={{
							width: adjustmentAction !== 'CUSTOM' ? '33%' : '',
							minHeight: '40px',
							alignSelf: 'center',
						}}
						marginRight='5vw'
					>
						<InputLabel id='adjustment-action-select-label'>Adjustment Action</InputLabel>
						<Select
							labelId='adjustment-action-select-label'
							value={adjustmentAction}
							onChange={(e) => setAdjustmentAction(e.target.value)}
							sx={{
								width: '16vw',
							}}
							data-cy='adjustment-action-select-menu'
						>
							{Object.keys(InvoiceAdjustmentActionTypeEnum).map((key) => (
								<MenuItem key={key} value={key}>
									{InvoiceAdjustmentActionTypeEnum[key]}
								</MenuItem>
							))}
						</Select>
					</Box>
					{adjustmentAction !== 'CUSTOM' && renderAdjustmentTypeDropdown()}
				</Box>
				<Box
					style={{
						width: adjustmentAction !== 'CUSTOM' ? '43%' : '',
						minHeight: '40px',
						alignSelf: 'center',
						marginTop: 0,
					}}
				>
					<TableContainer component={Paper}>
						<Table aria-label='tax-select-table'>
							<TableHead>
								<TableRow>
									<TableCell className='tax-select-mid-cell'>
										{/* placeholder for row headers */}
									</TableCell>
									<TableCell className='tax-select-mid-cell'>Net Amount</TableCell>
									<TableCell className='tax-select-large-cell'>Tax 1</TableCell>
									<TableCell className='tax-select-small-cell'>Tax 2</TableCell>
									<TableCell className='tax-select-small-cell'>Tax 3</TableCell>
									<TableCell className='tax-select-small-cell'>Tax 4</TableCell>
									<TableCell className='tax-select-small-cell'>Gross Amount</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{renderInitialAmounts()}
								{renderAdjustmentRow()}
								{renderAdjustedAmounts()}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</>
		);
	}

	function maybeRenderSaveAndCancel() {
		const somethingIsLoading = adjustInvoiceLoading || manuallyAdjustInvoiceLoading || invoiceAmountsLoading;
		const adjustmentInProgess = adjustInvoiceLoading || manuallyAdjustInvoiceLoading;
		if (somethingIsLoading) {
			return (
				<Box display='flex' justifyContent='flex-end'>
					<CircularProgress />
				</Box>
			);
		}
		return (
			<Box display='flex' justifyContent='flex-end'>
				<Button onClick={() => setShowTable(false)} disabled={adjustmentInProgess} data-cy='cancel-button'>
					Cancel
				</Button>
				<Button
					disabled={somethingIsLoading || (!adjustmentType && adjustmentAction !== 'CUSTOM')}
					onClick={handleSubmit}
					data-cy='save-button'
				>
					Save
				</Button>
			</Box>
		);
	}

	if (invoiceAmountsLoading) {
		return <CircularProgress style={{ height: '20px', width: '20px' }} />;
	}

	return (
		<Card sx={{ marginLeft: 4, marginTop: 1 }}>
			<CardContent
				sx={{
					padding: '10px !important',
				}}
			>
				{maybeRenderSaveAndCancel()}
				<Box display='flex' flexDirection='row' justifyContent='start'>
					{maybeRenderAdjustmentDetails()}
				</Box>
				{adjustmentAction === 'CUSTOM' && (
					<Box display='flex' flexDirection='row' justifyContent='start' paddingTop='40px'>
						To adjust a line item, expand a row from the table below and enter an adjustment amount.
					</Box>
				)}
			</CardContent>
		</Card>
	);
};
